"use client";

import { useEffect, useState } from "react";
import { useStudio } from "@/contexts/studio-context";

export interface PianoPressSequenceState {
  progress: number;
  complete: boolean;
}

// Subscribe to piano "press" events and report progress through an ordered
// sequence of MIDI notes. Each press advances the cursor when it matches the
// expected midi; a wrong press resets the cursor (with a one-step grace if the
// wrong note matches the very first expected midi).
export function usePianoPressSequence(
  expected: readonly number[],
  options: { enabled?: boolean } = {},
): PianoPressSequenceState {
  const { enabled = true } = options;
  const { studio } = useStudio();
  const [progress, setProgress] = useState(0);
  const [trackedExpected, setTrackedExpected] = useState(expected);

  // React-recommended pattern: reset state when a prop changes by comparing
  // during render and calling setState. React batches the update.
  if (trackedExpected !== expected) {
    setTrackedExpected(expected);
    setProgress(0);
  }

  useEffect(() => {
    if (!enabled || !studio || expected.length === 0) {
      return;
    }

    const piano = studio.getPiano();

    return piano.on((event) => {
      if (event.type !== "press") {
        return;
      }

      const midi = event.note.toMidi();

      setProgress((pos) => {
        if (pos >= expected.length) {
          return pos;
        }

        if (midi === expected[pos]) {
          return pos + 1;
        }

        if (midi === expected[0]) {
          return 1;
        }

        return 0;
      });
    });
  }, [enabled, studio, expected]);

  return {
    progress,
    complete: progress >= expected.length,
  };
}
